/// <amd-module name="TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotReducers" />
import produce from "immer";
import { PermissionTrace, SubjectPermission } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { generateRowId, generateRowIdDirect, getPermissionDisplayString } from "TFS/Admin/Common/Util/PermissionsHelper";
import { IPermissionsPivotState } from "TFS/Admin/Groups/Common/Contracts";
import { PermissionsPivotActions, PermissionsPivotActionTypes } from "./PermissionsPivotActions";
import { PermissionChangeStatus } from "TFS/Admin/Groups/Common/Constants";

export function permissionsPivotReducer(state: IPermissionsPivotState, action: PermissionsPivotActions): IPermissionsPivotState {
    return produce(state || {}, draft => {
        switch (action.type) {
            case PermissionsPivotActionTypes.InitView: {
                draft.data = {
                    permissions: undefined,
                    permissionTraces: {},
                    selectedPermission: undefined,
                    selectedPermissionChangeStatus: {}
                };
                draft.messageCardText = undefined;
                draft.loading = true;
                break;
            }

            case PermissionsPivotActionTypes.DisposeView: {
                // @ts-ignore TypeScript 4.0 upgrade
                delete draft.data;
                break;
            }

            case PermissionsPivotActionTypes.InitialPermissionsLoadSucceeded: {
                draft.data.permissions = action.payload.permissions;
                draft.loading = false;
                draft.isProjectScope = action.payload.permissions.isProjectScope;
                break;
            }

            case PermissionsPivotActionTypes.InitialPermissionsLoadFailed: {
                draft.loading = false;
                break;
            }

            case PermissionsPivotActionTypes.UpdatePermission: {
                const { namespaceId, token, permissionBit, newValue, newPermissionInherited, newPermissionGranted } = action.payload;

                if (draft.data.permissions) {
                    for (var i = 0; i < draft.data.permissions.subjectPermissions.length; i++) {
                        if (
                            draft.data.permissions.subjectPermissions[i].namespaceId == namespaceId &&
                            draft.data.permissions.subjectPermissions[i].token == token &&
                            draft.data.permissions.subjectPermissions[i].bit == permissionBit
                        ) {
                            draft.data.permissions.subjectPermissions[i].effectivePermissionValue = newValue;
                            draft.data.permissions.subjectPermissions[i].permissionDisplayString = getPermissionDisplayString(newValue);

                            draft.data.permissions.subjectPermissions[i].isPermissionInherited =
                                newPermissionInherited == undefined ? false : newPermissionInherited;
                            draft.data.permissions.subjectPermissions[i].isPermissionGrantedBySystem =
                                newPermissionGranted == undefined ? false : newPermissionGranted;

                            let rowId = generateRowIdDirect(namespaceId, permissionBit);
                            draft.data.selectedPermissionChangeStatus[rowId] = PermissionChangeStatus.Saved;
                            break;
                        }
                    }
                }
                break;
            }

            case PermissionsPivotActionTypes.SelectPermission: {
                draft.data.selectedPermission = action.payload;
                break;
            }

            case PermissionsPivotActionTypes.PickListActive: {
                let rowId = generateRowId(action.payload);
                draft.data.selectedPermissionChangeStatus[rowId] = PermissionChangeStatus.None;
                break;
            }

            case PermissionsPivotActionTypes.RemovePermission: {
                const { namespaceId, permissionsBit } = action.payload;
                let rowId = generateRowIdDirect(namespaceId, permissionsBit);
                draft.data.selectedPermissionChangeStatus[rowId] = PermissionChangeStatus.InProgress;
                break;
            }

            case PermissionsPivotActionTypes.SetPermission: {
                const { namespaceId, permissionBit } = action.payload.updates;
                let rowId = generateRowIdDirect(namespaceId, permissionBit);
                draft.data.selectedPermissionChangeStatus[rowId] = PermissionChangeStatus.InProgress;
                break;
            }

            case PermissionsPivotActionTypes.UpdatePermissionFailed: {
                const { namespaceId, permissionBit } = action.payload;
                let rowId = generateRowIdDirect(namespaceId, permissionBit);
                draft.data.selectedPermissionChangeStatus[rowId] = PermissionChangeStatus.Error;
                break;
            }

            case PermissionsPivotActionTypes.TracePermissionsSucceeded: {
                updatePermissionTraces(draft, action.payload.permissions, action.payload.traces);
                break;
            }

            case PermissionsPivotActionTypes.updateSingleTracePermissionSucceeded: {
                let rowId = generateRowId(action.payload.permission);

                state.data.permissionTraces[rowId] = action.payload.trace;
                break;
            }

            case PermissionsPivotActionTypes.PermissionOperationStatus: {
                var { resultMessage, severity } = action.payload;
                draft.messageCardText = resultMessage;
                draft.messageCardSeverity = severity;
                break;
            }

            case PermissionsPivotActionTypes.DismissMessageCard: {
                draft.messageCardText = undefined;
                break;
            }

            default:
                return state;
        }
    });
}

function updatePermissionTraces(state: IPermissionsPivotState, permissions: SubjectPermission[], traces: PermissionTrace[]) {
    if (permissions.length != traces.length || traces.length == 0) {
        return;
    }

    const { permissionTraces } = state.data;
    for (let i = 0; i < permissions.length; ++i) {
        permissionTraces[generateRowId(permissions[i])] = traces[i];
    }
}
